import { ReactElement, useMemo, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { TabContext, TabList } from '@mui/lab'
import { Box, Tab } from '@mui/material'
import { GenericErrorBoundaryFallback } from '@ui/errors'
import StyledTabPanel from '@ui/navigation/StyledTabPanel'
import { useSnackbar } from '@ui/snackbar'
import { useBlockAction } from '@editorHooks'
import {
  checkSettingsBlockAction,
  CheckSettingsSectionKey,
} from '../MultiEditCheckSettings/constants'
import { MultiEditCheckSettingsElement } from '../MultiEditCheckSettings/types'
import { CheckSettingsTab } from './tabs'

interface Props {
  tabs: CheckSettingsTab[]
  elements: MultiEditCheckSettingsElement[]
}

/**
 * Puts the check settings sections behind tabs, mirroring the single-element
 * detail pane, so the multi-edit drawer stays short no matter how many settings
 * groups apply to the selection.
 *
 * Only the selected panel is mounted, which keeps one form and one live
 * recompute in flight — switching away from unsaved changes is refused rather
 * than discarding them.
 */
const MultiEditCheckSettingsTabs = ({ tabs, elements }: Props): ReactElement | null => {
  const { enqueueSnackbar } = useSnackbar()

  const [tab, setTab] = useState<CheckSettingsSectionKey | null>(null)

  const resolvedTab = useMemo(() => {
    if (tab && tabs.some(({ key }) => key === tab)) return tab
    return tabs[0]?.key ?? null
  }, [tab, tabs])

  // Only the resolved (mounted) tab can be dirty, so its own block action is what
  // switching away from it must check.
  const { isBlockedFn } = useBlockAction(checkSettingsBlockAction(resolvedTab ?? ''), 'subscriber')

  if (!resolvedTab) return null

  // MUI's TabList onChange only ever hands back the `value` of one of the
  // Tabs we rendered below, each set to a CheckSettingsSectionKey.
  const handleChange = (value: CheckSettingsSectionKey) => {
    if (isBlockedFn()) {
      enqueueSnackbar(
        'Bitte speichern oder verwerfen Sie die Nachweiseinstellungen, bevor Sie den Tab wechseln',
        { variant: 'warning' },
      )
      return
    }
    setTab(value)
  }

  return (
    <TabContext value={resolvedTab}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', width: '100%' }}>
        <TabList
          onChange={(_event, value) => handleChange(value as CheckSettingsSectionKey)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 32,
            '& .MuiTab-root': { minHeight: 32, textTransform: 'none', py: 0.5 },
          }}
        >
          {tabs.map(({ key, label }) => (
            <Tab
              key={key}
              label={label}
              value={key}
              data-cy={`multi-edit-check-settings-tab-${key}`}
            />
          ))}
        </TabList>
      </Box>

      {tabs.map(({ key, render }) => (
        <StyledTabPanel key={key} value={key} sx={{ padding: 0 }}>
          <ErrorBoundary FallbackComponent={GenericErrorBoundaryFallback}>
            {render(elements)}
          </ErrorBoundary>
        </StyledTabPanel>
      ))}
    </TabContext>
  )
}

export default MultiEditCheckSettingsTabs
